/**
 * Payments-domain types (flow-09: payment, arrears, token & permanent unlock;
 * flow-28 duplicate intake; flow-29 refund path).
 *
 * CONTRACT NOTE (handoff TASK-009 — backend being built in parallel):
 * Base paths follow contracts/api/v1/payments.yaml (POST/GET /payments).
 * The flow-09 extensions below are the AGREED contract surface the backend
 * worker is building against — do not rename fields without syncing:
 *   - All money fields are INTEGER TZS CENTS (suffix `_cents`).
 *   - GET /payments/:id returns the full chain: verification + allocation +
 *     token (null while that stage is pending).
 *   - The full token is returned only to the recording device at issuance or
 *     explicit regenerate (standing rule: full token never logged server-side).
 *   - GET /customers?search= extends the existing customers list endpoint with
 *     a `search` param (name / customer_number / phone).
 *   - /payment-exceptions* is the new PaymentException queue (REC-YYYY-####).
 */

// ── Shared enums ─────────────────────────────────────────────────────────────

export type PaymentMethod = "mobile_money" | "ussd" | "office_cash" | "agent_collection";

/** Picker order on S1 (handoff: provider / USSD / office cash / agent). */
export const PAYMENT_METHODS_ORDER: PaymentMethod[] = [
  "mobile_money",
  "ussd",
  "office_cash",
  "agent_collection",
];

export const PAYMENT_METHOD_LABELS: Record<PaymentMethod, string> = {
  mobile_money: "M-Pesa",
  ussd: "USSD *150*01#",
  office_cash: "Office cash",
  agent_collection: "Agent collection",
};

/** Reference input label per method (provider ref vs paper receipt). */
export const PAYMENT_METHOD_REF_LABELS: Record<PaymentMethod, string> = {
  mobile_money: "Provider reference (M-Pesa)",
  ussd: "USSD reference",
  office_cash: "Receipt no.",
  agent_collection: "Agent receipt no.",
};

export type PaymentStatus = "pending" | "verified" | "duplicate" | "invalid";

// ── Payment chain (verification → allocation → token) ────────────────────────

export interface PaymentVerification {
  /** Provider reference lookup (null while pending). */
  reference_found: boolean | null;
  /** Webhook/recording signature valid (null while pending). */
  signature_valid: boolean | null;
  /** Idempotency: first time this reference+signature was seen. */
  first_seen: boolean | null;
  outcome: "pending" | "verified" | "duplicate" | "invalid";
  failure_reason: string | null;
}

/** Arrears-first allocation receipt (transactional server-side). */
export interface PaymentAllocation {
  amount_cents: number;
  /** Settled first — always before activation days. */
  arrears_applied_cents: number;
  /**
   * CONTRACT SEMANTIC WARNING (QC TASK-QC-002 F8): the backend currently
   * populates this with the contract's remaining principal, NOT this
   * payment's remainder after arrears (the design-receipt semantic). S3
   * derives the receipt's "Remaining" row as amount_cents − arrears_applied_cents
   * from exact wire values instead. Kept on the type because it is on the wire.
   */
  remaining_cents: number;
  /** Per-day rate from the plan tier. */
  rate_per_day_cents: number;
  /** Floor days; 0 is valid (arrears-only or short of installment). */
  activation_days: number;
  /** Sub-day remainder kept as customer credit. */
  credit_cents: number;
  arrears_balance_cents: number;
  /** ISO date; null when no days were purchased. */
  new_paid_through_date: string | null;
  fully_paid: boolean;
  allocated_at: string;
  /** Installment token gate */
  installment_min_cents?: number;
  installment_credit_after_cents?: number;
  shortfall_cents?: number;
  token_eligible?: boolean;
  remaining_for_token_tzs?: number;
}

export type TokenDeliveryState = "pending" | "pushed" | "confirmed";
export type SmsDeliveryState = "pending" | "sent" | "delivered" | "failed";

export interface PaymentToken {
  kind: "time_bound" | "permanent_unlock";
  /** FULL token — present only in the issuance/regenerate response on the
      recording device; null on plain re-reads (never logged server-side). */
  token: string | null;
  token_masked: string;
  /** Time-bound only. */
  valid_days: number | null;
  /** ISO date; time-bound only. */
  valid_until: string | null;
  /** Device push lifecycle: pending → pushed → confirmed-by-device. */
  state: TokenDeliveryState;
  permanent_unlock: {
    certificate_number: string;
    unlocked_at: string;
    payments_made: number;
    payments_total: number;
  } | null;
  sms: {
    to: string | null;
    state: SmsDeliveryState;
    sent_at: string | null;
  } | null;
}

export interface Payment {
  /** NULL on invalid/unmatched intake — by design no Payment row can exist;
      the exception (REC-YYYY-####) is the reference (QC TASK-QC-002 F2). */
  id: number | null;
  /** Receipt reference, e.g. "PAY-2026-3118"; NULL on invalid intake. */
  payment_number: string | null;
  customer_id: number;
  customer_name: string | null;
  contract_id: number | null;
  contract_number: string | null;
  amount_cents: number;
  method: PaymentMethod;
  provider_reference: string;
  status: PaymentStatus;
  recorded_by_id: number | null;
  created_at: string;
  verified_at: string | null;
  verification: PaymentVerification | null;
  allocation: PaymentAllocation | null;
  token: PaymentToken | null;
  /** Set when status=duplicate — the ORIGINAL payment (no double-apply). */
  duplicate_of_payment_id: number | null;
  duplicate_of_payment_number: string | null;
  /** Set when status=invalid — reconciliation exception (REC-YYYY-####). */
  exception_id: number | null;
  exception_number: string | null;
}

// ── Requests / responses ─────────────────────────────────────────────────────

export interface RecordPaymentRequest {
  customer_id: number;
  /** Optional — server resolves the active contract when omitted. */
  contract_id?: number;
  amount_cents: number;
  method: PaymentMethod;
  provider_reference: string;
}

export interface RecordPaymentResponse {
  message: string;
  payment: Payment;
  /** True when the Idempotency-Key replayed an earlier record. */
  replayed?: boolean;
}

export interface PaymentDetailResponse {
  payment: Payment;
}

export interface PaymentTokenResponse {
  message: string;
  token: PaymentToken;
}

// ── Customer search (S1 lookup + S7 candidate matching) ─────────────────────

export interface CustomerSearchRow {
  id: number;
  customer_number: string;
  name: string;
  ward: string | null;
  phone: string;
  active_contract: {
    id: number;
    contract_number: string;
    plan_name: string | null;
    arrears_cents: number;
  } | null;
}

export interface CustomerSearchResponse {
  customers: CustomerSearchRow[];
  total: number;
}

// ── S5 customer timeline ─────────────────────────────────────────────────────

export type TimelineEntryType =
  | "payment_received"
  | "arrears_applied"
  | "token_generated"
  | "sms_sent"
  | "permanent_unlock"
  | "payment_due";

export interface PaymentTimelineEntry {
  id: number;
  type: TimelineEntryType;
  state: "done" | "pending";
  /** ISO timestamp; null for the pending next-due entry. */
  at: string | null;
  title: string;
  /** Integer TZS cents when the entry carries an amount. */
  amount_cents: number | null;
  /** Preformatted extras, e.g. "M-Pesa ref TZK5P9W1QX", "7 activation days". */
  meta: string | null;
}

export interface PaymentTimelineResponse {
  customer: { id: number; name: string; contract_number: string | null };
  entries: PaymentTimelineEntry[];
}

// ── S6/S7 reconciliation exceptions ─────────────────────────────────────────

export type ExceptionReason = "invalid_ref" | "duplicate" | "signature_fail" | "unmatched_customer";
export type ExceptionStatus = "open" | "resolved";
export type ExceptionResolution = "matched" | "marked_duplicate" | "refunded";

export const EXCEPTION_REASON_LABELS: Record<ExceptionReason, string> = {
  invalid_ref: "Invalid ref",
  duplicate: "Duplicate",
  signature_fail: "Signature fail",
  unmatched_customer: "Unmatched customer",
};

export interface ExceptionAuditEntry {
  at: string;
  message: string;
}

export interface PaymentException {
  id: number;
  /** e.g. "REC-2026-0517". */
  exception_number: string;
  payment_id: number | null;
  received_at: string;
  /** Origin channel, e.g. "M-Pesa webhook", "Office cash". */
  channel: string;
  reference: string;
  amount_cents: number;
  payer_phone: string | null;
  reason: ExceptionReason;
  failure_detail: string | null;
  status: ExceptionStatus;
  resolved_at: string | null;
  resolution: ExceptionResolution | null;
  resolution_note: string | null;
  /** Present when reason=duplicate — the original payment to link. */
  original_payment_id: number | null;
  original_payment_number: string | null;
  audit: ExceptionAuditEntry[];
}

export interface PaymentExceptionListResponse {
  exceptions: PaymentException[];
  total: number;
}

export interface PaymentExceptionDetailResponse {
  exception: PaymentException;
}

export type ExceptionResolveRequest =
  | { action: "match"; contract_id: number }
  | { action: "mark_duplicate"; original_payment_number: string }
  /** Note is REQUIRED for refunds (flow-29; acceptance criterion 4). */
  | { action: "refund"; note: string };

export interface ExceptionResolveResponse {
  message: string;
  exception: PaymentException;
}
